export type PriceParts = {
  sign: string;
  symbolBefore: string | null;
  whole: string;
  decimal: string | null;
  fraction: string | null;
  symbolAfter: string | null;
};

function addPart(parts: PriceParts, part: Intl.NumberFormatPart) {
  switch (part.type) {
    case "minusSign":
    case "plusSign":
      parts.sign += part.value;
      break;
    case "currency":
      if (parts.whole) parts.symbolAfter = part.value;
      else parts.symbolBefore = part.value;
      break;
    case "integer":
    case "group":
      parts.whole += part.value;
      break;
    case "decimal":
      parts.decimal = part.value;
      break;
    case "fraction":
      parts.fraction = (parts.fraction ?? "") + part.value;
      break;
  }
}

function compactCurrencyOptions(compact: boolean) {
  if (!compact) return {};
  return { minimumFractionDigits: 0, maximumFractionDigits: 0 };
}

/**
 * Split a localized currency amount into the pieces the `<Price>` component styles separately:
 * the currency symbol (before or after the number, per locale), the whole part with its group
 * separators, and the fraction. Spacing literals between symbol and number are dropped because the
 * symbol renders as a superscript.
 *
 * `compact` truncates to whole units (card and tab badges); otherwise the currency's own minor
 * digits are always shown (R$249,44, R$230,00, ¥1,200).
 */
export function priceParts(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
  locale: string | undefined,
  options: { compact?: boolean } = {},
): PriceParts | null {
  if (amount == null || amount === "" || !currency) return null;
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return null;

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    ...compactCurrencyOptions(options.compact === true),
  });

  const parts: PriceParts = {
    sign: "",
    symbolBefore: null,
    whole: "",
    decimal: null,
    fraction: null,
    symbolAfter: null,
  };
  for (const part of formatter.formatToParts(options.compact ? Math.trunc(value) : value)) {
    addPart(parts, part);
  }
  return parts;
}
